import React, { useState, useEffect } from 'react';
import {
  FiInfo,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiX,
  FiCheck,
  FiMapPin,
  FiImage,
  FiUser,
  FiNavigation,
  FiEdit,
  FiSave
} from 'react-icons/fi';
import { FaCar, FaMapMarkerAlt, FaRupeeSign } from 'react-icons/fa';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../../libs/axios';
import { useAuth } from '../../../provider/auth.provider';
import validator from 'validator';
import { formatDate } from '../../activities/edit';
import EntityLogs from '../../../utils/logs.utils';
import { LuActivity } from 'react-icons/lu';

interface Visit {
  id: number;
  project_site_id?: number | null;
  cab_hiring_id: number;
  visit_number: number;
  visit_selfie_with_cab: string;
  visit_client_proof: string;
  visit_latitude?: string | number | null;
  visit_longitude?: string | number | null;
  visit_address?: string | null;
  created_at: string;
  updated_at: string;
  project_site?: {
    id: number;
    site_id: string;
    site_name: string;
    site_latitude: string;
    site_longitude: string;
  } | null;
}

interface CabHiring {
  id: number;
  engineer_id: number;
  project_site_id?: number | null;
  association_type: string;
  trip_purpose: string;
  trip_start_date: string;
  trip_end_date?: string | null;
  start_latitude?: string | null;
  start_longitude?: string | null;
  end_latitude?: string | null;
  end_longitude?: string | null;
  cab_start_reading: number;
  cab_start_number_selfie?: string | null;
  cab_start_model_selfie?: string | null;
  cab_visit: Visit[];
  cab_end_reading?: number | null;
  cab_end_number_selfie?: string | null;
  cab_end_model_selfie?: string | null;
  running_kms?: number | null;
  toll_charges?: number | null;
  other_charges?: number | null;
  hiring_total_amount?: number | null;
  hiring_per_km_amount?: number | null;
  hiring_remarks?: string | null;
  bill_photo?: string | null;
  toll_photo?: string | null;
  payble_entity?: string | null;
  payble_entity_name?: string | null;
  status: string;
  approved_by?: number | null;
  approved_at?: string | null;
  approved_amount?: number | null;
  rejection_reason?: string | null;
  rejected_at?: string | null;
  created_at: string;
  updated_at: string;
  engineer: {
    id: number;
    first_name: string;
    last_name: string;
    employee_code?: string;
  };
  project_site?: {
    id: number;
    site_id: string;
    site_name: string;
  } | null;
  rejector?: {
    id: number;
    first_name: string;
    last_name: string;
    association_type: string;
  } | null;
  approver?: {
    id: number;
    first_name: string;
    last_name: string;
    association_type: string;
  } | null;
}

interface LocationData {
  formatted_address: string;
  coordinates: {
    lat: number;
    lng: number;
  };
}

const CabHiringView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [trip, setTrip] = useState<CabHiring | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('details');
  const [showApprovalDrawer, setShowApprovalDrawer] = useState<boolean>(false);
  const [showRejectDrawer, setShowRejectDrawer] = useState<boolean>(false);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [payableEntity, setPayableEntity] = useState<string>('');
  const [payableEntityName, setPayableEntityName] = useState<string>('');
  const [approvedAmount, setApprovedAmount] = useState<string>('');
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [showVisitModal, setShowVisitModal] = useState<boolean>(false);
  const [visitLocation, setVisitLocation] = useState<LocationData | null>(null);
  const [locationLoading, setLocationLoading] = useState<boolean>(false);

  // Edit mode state
  const [showEditDrawer, setShowEditDrawer] = useState<boolean>(searchParams.get('edit') === 'true');
  const [editForm, setEditForm] = useState({
    toll_charges: '',
    other_charges: '',
    hiring_remarks: '',
  });
  const [editLoading, setEditLoading] = useState<boolean>(false);

  const [errors, setErrors] = useState<any>({});
  const [touched, setTouched] = useState<any>({});
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const tabs = [
    { id: 'details', label: 'Trip Details', icon: FiInfo },
    { id: 'visits', label: 'Visits', icon: FiMapPin },
    { id: 'amounts', label: 'Amounts', icon: FaRupeeSign },
    { id: 'images', label: 'Images', icon: FiImage },
    { id: 'logs', label: 'Logs', icon: LuActivity },
  ];

  useEffect(() => {
    fetchTripData();
  }, [id]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !actionLoading) {
        if (showApprovalDrawer) setShowApprovalDrawer(false);
        if (showRejectDrawer) setShowRejectDrawer(false);
        if (showVisitModal) setShowVisitModal(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showApprovalDrawer, showRejectDrawer, showVisitModal, actionLoading]);

  const fetchTripData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/claims/cab-hiring/${id}`);
      const data = response.data.data;
      setTrip(data);
      // Populate edit form with current values
      setEditForm({
        toll_charges: data?.toll_charges != null ? String(data.toll_charges) : '',
        other_charges: data?.other_charges != null ? String(data.other_charges) : '',
        hiring_remarks: data?.hiring_remarks || '',
      });
    } catch (error) {
      console.error('Error fetching trip:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSave = async () => {
    if (!trip) return;
    try {
      setEditLoading(true);
      await axiosInstance.put(`/claims/cab-hiring/${id}/update-submitted`, {
        toll_charges: editForm.toll_charges !== '' ? Number(editForm.toll_charges) : undefined,
        other_charges: editForm.other_charges !== '' ? Number(editForm.other_charges) : undefined,
        hiring_remarks: editForm.hiring_remarks,
      });
      await fetchTripData();
      setShowEditDrawer(false);
      navigate(`/claims/cab-hiring/${id}`, { replace: true });
    } catch (error) {
      console.error('Error updating cab hiring:', error);
    } finally {
      setEditLoading(false);
    }
  };

  // Function to fetch location from coordinates
  const fetchLocationFromCoordinates = async (lat: number, lng: number): Promise<LocationData | null> => {
    try {
      const response = await axiosInstance.post('/maps/reverse-geocode', {
        lat,
        lng
      });
      
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching location:', error);
      return null;
    }
  };

  const handleVisitClick = async (visit: Visit) => {
    setSelectedVisit(visit);
    setShowVisitModal(true);
    setLocationLoading(true);
    setVisitLocation(null);

    // Fetch location for the visit
    const lat = Number(visit.visit_latitude ?? visit.project_site?.site_latitude);
    const lng = Number(visit.visit_longitude ?? visit.project_site?.site_longitude);
    
    if (!isNaN(lat) && !isNaN(lng)) {
      const location = await fetchLocationFromCoordinates(lat, lng);
      setVisitLocation(location);
    }
    
    setLocationLoading(false);
  };

  const validateRejectionReason = (value: string): string => {
    if (!value || validator.isEmpty(value.trim())) {
      return 'Rejection reason is required';
    }

    if (!validator.isLength(value.trim(), { min: 10, max: 500 })) {
      return 'Rejection reason must be between 10 and 500 characters';
    }

    // Check if it contains only valid characters (alphanumeric, spaces, basic punctuation)
    if (!validator.matches(value, /^[a-zA-Z0-9\s.,!?'-]+$/)) {
      return 'Rejection reason contains invalid characters';
    }

    return '';
  };

  const validatePayableEntity = (value: string): string => {
    if (!value || validator.isEmpty(value.trim())) {
      return 'Payable entity is required';
    }
    return '';
  };

  const validatePayableEntityName = (value: string, entity: string): string => {
    if (entity === 'cab_vendor') {
      if (!value || validator.isEmpty(value.trim())) {
        return 'Cab vendor name is required';
      }
      if (!validator.isLength(value.trim(), { min: 2, max: 100 })) {
        return 'Name must be between 2 and 100 characters';
      }
    }
    return '';
  };

  const validateRejectForm = (): boolean => {
    const newErrors: any = {};

    const reasonError = validateRejectionReason(rejectionReason);
    if (reasonError) {
      newErrors.rejectionReason = reasonError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateApprovedAmount = (value: string, maxAmount: number): string => {
    if (value === '' || value === undefined) return '';
    const num = Number(value);
    if (isNaN(num) || num < 0) return 'Approved amount must be a non-negative number';
    if (num > maxAmount) return `Approved amount cannot exceed the total amount (₹${maxAmount.toLocaleString('en-IN')})`;
    return '';
  };

  const validateApprovalForm = (): boolean => {
    const newErrors: any = {};

    const entityError = validatePayableEntity(payableEntity);
    if (entityError) {
      newErrors.payableEntity = entityError;
    }

    const nameError = validatePayableEntityName(payableEntityName, payableEntity);
    if (nameError) {
      newErrors.payableEntityName = nameError;
    }

    const totalAmount = getCabHiringTotalAmount(trip);
    const amountError = validateApprovedAmount(approvedAmount, totalAmount);
    if (amountError) {
      newErrors.approvedAmount = amountError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApprove = async () => {
    if (!validateApprovalForm()) return;

    try {
      setActionLoading(true);
      const payload: any = {
        id: Number(id),
        status: 'approved',
        payble_entity: payableEntity
      };

      if (payableEntity === 'cab_vendor') {
        payload.payble_entity_name = payableEntityName.trim();
      }

      if (approvedAmount !== '') {
        payload.approved_amount = Number(approvedAmount);
      }

      await axiosInstance.put('/claims/cab-hiring/approve-reject', payload);
      await fetchTripData();
      setShowApprovalDrawer(false);
      setPayableEntity('');
      setPayableEntityName('');
      setApprovedAmount('');
      setErrors({});
      setTouched({});
    } catch (error) {
      console.error('Error approving trip:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!validateRejectForm()) return;

    try {
      setActionLoading(true);
      await axiosInstance.put('/claims/cab-hiring/approve-reject', {
        id: Number(id),
        status: 'rejected',
        rejection_reason: rejectionReason.trim()
      });
      await fetchTripData();
      setShowRejectDrawer(false);
      setRejectionReason('');
      setErrors({});
      setTouched({});
    } catch (error) {
      console.error('Error rejecting trip:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (amount?: number | null) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Mirrors the server: hiring_total_amount is stored as km x rate + toll + other charges,
  // so toll / other charges must not be added on top of it again.
  const getCabHiringKmCharges = (cabHiring?: CabHiring | null) => {
    const runningKms = Number(cabHiring?.running_kms ?? 0)
    const perKm = Number(cabHiring?.hiring_per_km_amount ?? 0)
    if (runningKms > 0 || perKm > 0) return Math.round(runningKms * perKm)
    return Math.max(
      Number(cabHiring?.hiring_total_amount ?? 0) - Number(cabHiring?.toll_charges ?? 0) - Number(cabHiring?.other_charges ?? 0),
      0,
    )
  };

  const getCabHiringTotalAmount = (cabHiring?: CabHiring | null) => {
    const runningKms = Number(cabHiring?.running_kms ?? 0)
    const perKm = Number(cabHiring?.hiring_per_km_amount ?? 0)
    if (runningKms > 0 || perKm > 0) {
      return Math.round(runningKms * perKm + Number(cabHiring?.toll_charges ?? 0) + Number(cabHiring?.other_charges ?? 0))
    }
    return Number(cabHiring?.hiring_total_amount ?? 0)
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'in_progress': { icon: FiClock, bgColor: 'bg-blue-100', textColor: 'text-blue-800', label: 'In Progress' },
      'completed': { icon: FiCheckCircle, bgColor: 'bg-gray-100', textColor: 'text-gray-800', label: 'Completed' },
      'claim_submitted': { icon: FiClock, bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', label: 'Submitted' },
      'approved': { icon: FiCheckCircle, bgColor: 'bg-green-100', textColor: 'text-green-800', label: 'Approved' },
      'rejected': { icon: FiAlertCircle, bgColor: 'bg-red-100', textColor: 'text-red-800', label: 'Rejected' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.in_progress;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.bgColor} ${config.textColor}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

 const renderDetailsTab = () => (
  <div className="space-y-4">
    <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
      <h3 className="text-base font-semibold text-gray-900 mb-4">Trip Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-gray-500">Engineer Name</label>
          <p className="text-sm text-gray-900 mt-1 font-semibold">{trip?.engineer.first_name} {trip?.engineer.last_name}</p>
          {trip?.engineer.employee_code && (
            <p className="text-xs text-gray-500 mt-0.5">{trip.engineer.employee_code}</p>
          )}
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500">Project Site</label>
          <p className="text-sm text-gray-900 mt-1">{trip?.project_site?.site_name || 'N/A'} ({trip?.project_site?.site_id || 'N/A'})</p>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500">Trip Purpose</label>
          <p className="text-sm text-gray-900 mt-1">{trip?.trip_purpose}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500">Running Distance</label>
          <p className="text-sm mt-1 font-bold text-blue-900">{trip?.running_kms || 0} km</p>
        </div>
        {trip?.payble_entity && (
          <div>
            <label className="text-xs font-medium text-gray-500">Payable Entity</label>
            <p className="text-sm text-gray-900 mt-1 capitalize">{trip.payble_entity.replace('_', ' ')}</p>
          </div>
        )}
        {trip?.payble_entity_name && (
          <div>
            <label className="text-xs font-medium text-gray-500">Payable Entity Name</label>
            <p className="text-sm text-gray-900 mt-1">{trip.payble_entity_name}</p>
          </div>
        )}
        {trip?.hiring_remarks && (
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-gray-500">Remarks</label>
            <p className="text-sm text-gray-900 mt-1">{trip.hiring_remarks}</p>
          </div>
        )}
      </div>
    </div>

    <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
      <h3 className="text-base font-semibold text-gray-900 mb-4">Trip Start Details</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-gray-500">Start Reading</label>
          <p className="text-sm text-gray-900 mt-1">{trip?.cab_start_reading} km</p>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500">Started At</label>
          <p className="text-sm text-gray-900 mt-1">{trip?.trip_start_date ? formatDate(trip.trip_start_date) : 'N/A'}</p>
        </div>
        {trip?.start_latitude && trip?.start_longitude && (
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-gray-500">Start Location</label>
            <p className="text-sm text-gray-900 mt-1">{trip.start_latitude}, {trip.start_longitude}</p>
          </div>
        )}
        {trip?.cab_start_number_selfie && (
          <div className="relative group max-w-xs">
            <label className="text-xs font-medium text-gray-500">Start Number Selfie</label>
            <div className="relative mt-2 overflow-hidden rounded border border-gray-200">
              <img src={trip.cab_start_number_selfie} alt="Start Number" className="w-36 h-28 object-contain bg-gray-50" />
              <button type="button" onClick={() => setLightboxSrc(trip!.cab_start_number_selfie!)}
                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-gray-800 text-xs font-medium px-3 py-1.5 rounded shadow-sm flex items-center gap-1">
                  <FiImage className="w-3 h-3" /> View Full Image
                </span>
              </button>
            </div>
          </div>
        )}
        {trip?.cab_start_model_selfie && (
          <div className="relative group max-w-xs">
            <label className="text-xs font-medium text-gray-500">Start Reading Kms</label>
            <div className="relative mt-2 overflow-hidden rounded border border-gray-200">
              <img src={trip.cab_start_model_selfie} alt="Start Reading Kms" className="w-36 h-28 object-contain bg-gray-50" />
              <button type="button" onClick={() => setLightboxSrc(trip!.cab_start_model_selfie!)}
                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-gray-800 text-xs font-medium px-3 py-1.5 rounded shadow-sm flex items-center gap-1">
                  <FiImage className="w-3 h-3" /> View Full Image
                </span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>

    {trip?.trip_end_date && (
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Trip End Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-500">End Reading</label>
            <p className="text-sm text-gray-900 mt-1">{trip.cab_end_reading ? `${trip.cab_end_reading} km` : 'Not ended'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Ended At</label>
            <p className="text-sm text-gray-900 mt-1">{formatDate(trip.trip_end_date)}</p>
          </div>
          {trip?.end_latitude && trip?.end_longitude && (
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-500">End Location</label>
              <p className="text-sm text-gray-900 mt-1">{trip.end_latitude}, {trip.end_longitude}</p>
            </div>
          )}
          {trip?.cab_end_number_selfie && (
            <div className="relative group max-w-xs">
              <label className="text-xs font-medium text-gray-500">End Number Selfie</label>
              <div className="relative mt-2 overflow-hidden rounded border border-gray-200">
                <img src={trip.cab_end_number_selfie} alt="End Number" className="w-36 h-28 object-contain bg-gray-50" />
                <button type="button" onClick={() => setLightboxSrc(trip!.cab_end_number_selfie!)}
                  className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-gray-800 text-xs font-medium px-3 py-1.5 rounded shadow-sm flex items-center gap-1">
                    <FiImage className="w-3 h-3" /> View Full Image
                  </span>
                </button>
              </div>
            </div>
          )}
          {trip?.cab_end_model_selfie && (
            <div className="relative group max-w-xs">
              <label className="text-xs font-medium text-gray-500">Selfie with End Kms</label>
              <div className="relative mt-2 overflow-hidden rounded border border-gray-200">
                <img src={trip.cab_end_model_selfie} alt="Selfie with End Kms" className="w-36 h-28 object-contain bg-gray-50" />
                <button type="button" onClick={() => setLightboxSrc(trip!.cab_end_model_selfie!)}
                  className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-gray-800 text-xs font-medium px-3 py-1.5 rounded shadow-sm flex items-center gap-1">
                    <FiImage className="w-3 h-3" /> View Full Image
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    )}

    {trip?.status === 'approved' && (
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Approval Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-500">Approved By</label>
            <p className="text-sm text-gray-900 mt-1 font-semibold">
              {trip?.approver ? `${trip.approver.first_name} ${trip.approver.last_name}` : 'N/A'}
            </p>
            {trip?.approver?.association_type && (
              <p className="text-xs text-gray-600 mt-1 capitalize">{trip.approver.association_type}</p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Approved At</label>
            <p className="text-sm text-gray-900 mt-1">{trip?.approved_at ? formatDate(trip.approved_at) : 'N/A'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Approved Amount</label>
            <p className="text-sm text-gray-900 mt-1 font-semibold">
              {trip?.approved_amount != null ? formatCurrency(trip.approved_amount) : formatCurrency(getCabHiringTotalAmount(trip))}
            </p>
          </div>
        </div>
      </div>
    )}

    {trip?.status === 'rejected' && (
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Rejection Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-500">Rejected By</label>
            <p className="text-sm text-gray-900 mt-1 font-semibold">
              {trip?.rejector ? `${trip.rejector.first_name} ${trip.rejector.last_name}` : 'N/A'}
            </p>
            {trip?.rejector?.association_type && (
              <p className="text-xs text-gray-600 mt-1 capitalize">{trip.rejector.association_type}</p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Rejected At</label>
            <p className="text-sm text-gray-900 mt-1">{trip?.rejected_at ? formatDate(trip.rejected_at) : 'N/A'}</p>
          </div>
          {trip?.rejection_reason && (
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-500">Rejection Reason</label>
              <p className="text-sm text-red-900 mt-1 bg-red-50 p-3 rounded">{trip.rejection_reason}</p>
            </div>
          )}
        </div>
      </div>
    )}
  </div>
);

  const handleStartClick = async () => {
    if (trip?.start_latitude && trip?.start_longitude) {
      setSelectedVisit(null);
      setShowVisitModal(true);
      setLocationLoading(true);
      setVisitLocation(null);

      const lat = parseFloat(trip.start_latitude);
      const lng = parseFloat(trip.start_longitude);
      
      if (!isNaN(lat) && !isNaN(lng)) {
        const location = await fetchLocationFromCoordinates(lat, lng);
        setVisitLocation(location);
      }
      
      setLocationLoading(false);
    }
  };

  const handleEndClick = async () => {
    if (trip?.end_latitude && trip?.end_longitude) {
      setSelectedVisit(null);
      setShowVisitModal(true);
      setLocationLoading(true);
      setVisitLocation(null);

      const lat = parseFloat(trip.end_latitude);
      const lng = parseFloat(trip.end_longitude);
      
      if (!isNaN(lat) && !isNaN(lng)) {
        const location = await fetchLocationFromCoordinates(lat, lng);
        setVisitLocation(location);
      }
      
      setLocationLoading(false);
    }
  };

  const renderVisitsTab = () => {
    const visits = trip?.cab_visit || [];
    const hasStartLocation = trip?.start_latitude && trip?.start_longitude;
    const hasEndLocation = trip?.end_latitude && trip?.end_longitude;
    const tripStarted = trip?.trip_start_date;
    const tripEnded = trip?.trip_end_date;

    return (
      <div className="space-y-4">
        <div className="bg-white rounded shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Trip Journey Timeline</h3>
              <p className="text-sm text-gray-600 mt-1">
                {visits.length} {visits.length === 1 ? 'visit' : 'visits'} • {trip?.running_kms || 0} km travelled
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="h-3 w-3 rounded-full bg-green-500"></div>
                <span className="text-gray-600">Start</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                <span className="text-gray-600">Visit</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-3 w-3 rounded-full bg-red-500"></div>
                <span className="text-gray-600">End</span>
              </div>
            </div>
          </div>
          
          {tripStarted ? (
            <div className="relative">
              {/* Vertical Timeline Line */}
              <div className="absolute left-8 top-6 bottom-6 w-1 bg-gradient-to-b from-green-400 via-blue-400 to-red-400 rounded-full"></div>

              {/* Start Location */}
              <div className="relative flex items-start mb-6">
                <div className="absolute left-8 -ml-3 mt-2">
                  <div className="h-6 w-6 rounded-full bg-green-500 border-4 border-white shadow-lg flex items-center justify-center">
                    <FiNavigation className="w-3 h-3 text-white" />
                  </div>
                </div>
                <div 
                  className={`ml-16 bg-white border-2 border-green-300 rounded p-5 flex-1 shadow-md hover:shadow-xl transition-all duration-300 ${
                    hasStartLocation ? 'cursor-pointer hover:border-green-500 hover:scale-[1.02]' : ''
                  }`}
                  onClick={hasStartLocation ? handleStartClick : undefined}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-green-100 p-2 rounded">
                        <FiNavigation className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="text-base font-bold text-green-900">Trip Start</h4>
                        <p className="text-xs text-green-700">Journey begins here</p>
                      </div>
                    </div>
                    {hasStartLocation && (
                      <div className="bg-green-100 text-green-700 text-xs font-medium px-3 py-1 rounded-full">
                        View Details →
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-green-100">
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Started At</p>
                      <p className="text-sm text-gray-900 mt-1 font-semibold">
                        {formatDate(trip?.trip_start_date || '')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Odometer Reading</p>
                      <p className="text-sm text-gray-900 mt-1 font-semibold">
                        {trip?.cab_start_reading} km
                      </p>
                    </div>
                  </div>
                  {!hasStartLocation && (
                    <div className="mt-3 text-xs text-gray-500 italic">
                      Location data not available
                    </div>
                  )}
                </div>
              </div>

              {/* Visits */}
              {visits.length > 0 ? (
                visits.map((visit, index) => (
                  <div key={visit.id} className="relative flex items-start mb-6">
                    <div className="absolute left-8 -ml-3 mt-2">
                      <div className="h-6 w-6 rounded-full bg-blue-500 border-4 border-white shadow-lg flex items-center justify-center">
                        <span className="text-xs font-bold text-white">{visit.visit_number}</span>
                      </div>
                    </div>
                    <div 
                      className="ml-16 bg-white border-2 border-blue-300 rounded p-5 flex-1 cursor-pointer shadow-md hover:shadow-xl hover:border-blue-500 hover:scale-[1.02] transition-all duration-300"
                      onClick={() => handleVisitClick(visit)}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-100 p-2 rounded">
                            <FaMapMarkerAlt className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="text-base font-bold text-blue-900">Visit #{visit.visit_number}</h4>
                            <p className="text-xs text-blue-700">Site inspection point</p>
                          </div>
                        </div>
                        <div className="bg-blue-100 text-blue-700 text-xs font-medium px-3 py-1 rounded-full">
                          View Details →
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-blue-100">
                        <div className="mb-2">
                          <p className="text-xs text-gray-500 font-medium">Site Name</p>
                          <p className="text-sm text-gray-900 mt-1 font-semibold">
                            {visit.project_site?.site_name || visit.visit_address || "No site linked"}
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs text-gray-500 font-medium">Site ID</p>
                            <p className="text-sm text-gray-900 mt-1">{visit.project_site?.site_id || "N/A"}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium">Visit Time</p>
                            <p className="text-sm text-gray-900 mt-1">
                              {new Date(visit.created_at).toLocaleTimeString('en-IN', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="relative flex items-center justify-center my-8">
                  <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded p-6 text-center">
                    <FaMapMarkerAlt className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 font-medium">No visits recorded</p>
                    <p className="text-xs text-gray-500 mt-1">Site visits will appear here</p>
                  </div>
                </div>
              )}

              {/* End Location */}
              {tripEnded && (
                <div className="relative flex items-start">
                  <div className="absolute left-8 -ml-3 mt-2">
                    <div className="h-6 w-6 rounded-full bg-red-500 border-4 border-white shadow-lg flex items-center justify-center">
                      <FiNavigation className="w-3 h-3 text-white transform rotate-180" />
                    </div>
                  </div>
                  <div 
                    className={`ml-16 bg-white border-2 border-red-300 rounded p-5 flex-1 shadow-md hover:shadow-xl transition-all duration-300 ${
                      hasEndLocation ? 'cursor-pointer hover:border-red-500 hover:scale-[1.02]' : ''
                    }`}
                    onClick={hasEndLocation ? handleEndClick : undefined}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="bg-red-100 p-2 rounded">
                          <FiNavigation className="w-5 h-5 text-red-600 transform rotate-180" />
                        </div>
                        <div>
                          <h4 className="text-base font-bold text-red-900">Trip End</h4>
                          <p className="text-xs text-red-700">Journey completed</p>
                        </div>
                      </div>
                      {hasEndLocation && (
                        <div className="bg-red-100 text-red-700 text-xs font-medium px-3 py-1 rounded-full">
                          View Details →
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-red-100">
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Ended At</p>
                        <p className="text-sm text-gray-900 mt-1 font-semibold">
                          {formatDate(trip?.trip_end_date || '')}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Odometer Reading</p>
                        <p className="text-sm text-gray-900 mt-1 font-semibold">
                          {trip?.cab_end_reading} km
                        </p>
                      </div>
                    </div>
                    {!hasEndLocation && (
                      <div className="mt-3 text-xs text-gray-500 italic">
                        Location data not available
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="bg-gray-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <FaCar className="w-10 h-10 text-gray-400" />
              </div>
              <p className="text-sm text-gray-600 font-medium">Trip not started yet</p>
              <p className="text-xs text-gray-500 mt-1">Timeline will be available once the trip begins</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAmountsTab = () => (
    <div className="space-y-4">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Cost Breakdown</h3>
        <div className="space-y-3">
          {(trip?.running_kms != null || trip?.hiring_per_km_amount != null) && (
            <div className="bg-gray-50 rounded p-3 mb-2">
              <p className="text-xs font-medium text-gray-500 mb-2">Trip Reference Data</p>
              <div className="flex justify-between text-xs text-gray-500">
                <span>Distance Travelled</span>
                <span>{trip?.running_kms || 0} km</span>
              </div>
              {trip?.hiring_per_km_amount != null && (
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Per KM Rate</span>
                  <span>{formatCurrency(trip?.hiring_per_km_amount)}</span>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">KM Charges</span>
            <span className="text-sm font-medium text-gray-900">
              {formatCurrency(getCabHiringKmCharges(trip))}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Toll Charges</span>
            <span className="text-sm font-medium text-gray-900">{formatCurrency(trip?.toll_charges)}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Other Charges</span>
            <span className="text-sm font-medium text-gray-900">{formatCurrency(trip?.other_charges)}</span>
          </div>
          <div className="flex justify-between py-3 border-t-2 border-gray-200 mt-2">
            <span className="text-base font-semibold text-gray-900">Total Amount</span>
            <span className="text-base font-bold text-blue-900">{formatCurrency(getCabHiringTotalAmount(trip))}</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Amount Summary</h3>
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Submitted Amount</span>
            <span className="text-sm font-medium text-gray-900">{formatCurrency(getCabHiringTotalAmount(trip))}</span>
          </div>
        </div>
      </div>

      {trip?.status === 'approved' && (
        <div className="bg-white rounded shadow-sm border border-green-200 p-4 bg-green-50">
          <h3 className="text-base font-semibold text-green-900 mb-4">Approval Details</h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-green-100">
              <span className="text-sm text-green-700">Approved Amount</span>
              <span className="text-base font-bold text-green-700">
                {trip?.approved_amount != null ? formatCurrency(trip.approved_amount) : formatCurrency(getCabHiringTotalAmount(trip))}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-green-100">
              <span className="text-sm text-green-700">Approved By</span>
              <span className="text-sm font-medium text-green-900">
                {trip?.approver ? `${trip.approver.first_name} ${trip.approver.last_name}` : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-sm text-green-700">Approved Date</span>
              <span className="text-sm font-medium text-green-900">
                {trip?.approved_at ? formatDate(trip.approved_at) : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      )}

      {(trip?.bill_photo || trip?.toll_photo) && (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Supporting Documents</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {trip?.bill_photo && renderImageCard(trip.bill_photo, 'Bill Photo', 'Bill')}
            {trip?.toll_photo && renderImageCard(trip.toll_photo, 'Toll Receipt', 'Toll')}
          </div>
        </div>
      )}
    </div>
  );

  const renderImageCard = (src: string, label: string, alt: string) => (
    <div className="relative group">
      <label className="text-xs font-medium text-gray-500 mb-2 block">{label}</label>
      <div className="relative overflow-hidden rounded border border-gray-200 bg-gray-50">
        <img src={src} alt={alt} className="w-full max-h-48 object-contain" />
        <button
          type="button"
          onClick={() => setLightboxSrc(src)}
          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200"
        >
          <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-gray-800 text-xs font-medium px-3 py-1.5 rounded shadow-sm flex items-center gap-1">
            <FiImage className="w-3 h-3" /> View Full Image
          </span>
        </button>
      </div>
    </div>
  );

  const renderImagesTab = () => (
    <div className="space-y-4">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Trip Images</h3>
        <div className="space-y-6">
          {/* Start Images */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Trip Start Images</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {trip?.cab_start_number_selfie && renderImageCard(trip.cab_start_number_selfie, 'Cab Number (Start)', 'Cab Number Start')}
              {trip?.cab_start_model_selfie && renderImageCard(trip.cab_start_model_selfie, 'Start Reading Kms', 'Start Reading Kms')}
            </div>
          </div>

          {/* End Images */}
          {(trip?.cab_end_number_selfie || trip?.cab_end_model_selfie) && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Trip End Images</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {trip?.cab_end_number_selfie && renderImageCard(trip.cab_end_number_selfie, 'Cab Number (End)', 'Cab Number End')}
                {trip?.cab_end_model_selfie && renderImageCard(trip.cab_end_model_selfie, 'Selfie with End Kms', 'Selfie with End Kms')}
              </div>
            </div>
          )}

          {!trip?.cab_start_number_selfie && !trip?.cab_start_model_selfie && !trip?.cab_end_number_selfie && !trip?.cab_end_model_selfie && (
            <div className="text-center py-8">
              <FiImage className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">No images available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading trip details...</p>
        </div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Trip Not Found</h2>
          <p className="text-gray-600 mb-4">The cab hiring trip you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/claims/cab-hiring')}
            className="px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900"
          >
            Back to Trips
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        {/* Header */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Cab Hiring Trip #{trip.id}</h1>
              <p className="text-sm text-gray-600 mt-1">{trip?.engineer.first_name} {trip?.engineer.last_name} - {trip.project_site?.site_name || 'N/A'}</p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-2">
                <div className="flex items-center gap-1">
                  <FaCar size={12} />
                  {trip.running_kms || 0} km
                </div>
                <div className="flex items-center gap-1">
                  <FaMapMarkerAlt size={12} />
                  {trip.cab_visit?.length || 0} visits
                </div>
                {trip.status === 'rejected' && trip.rejected_at && (
                  <div className="flex items-center gap-1 text-red-600">
                    <FiCalendar size={12} />
                    Rejected: {formatDate(trip.rejected_at)}
                  </div>
                )}
                {trip.status === 'rejected' && trip.rejector && (
                  <div className="flex items-center gap-1 text-red-600">
                    <FiUser size={12} />
                    {trip.rejector.first_name} {trip.rejector.last_name}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {getStatusBadge(trip.status)}
              {trip.status === 'claim_submitted' && (
                <>
                  <button
                    onClick={() => setShowApprovalDrawer(true)}
                    disabled={actionLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    <FiCheck className="w-4 h-4" />
                    Approve
                  </button>
                  <button
                    onClick={() => setShowRejectDrawer(true)}
                    disabled={actionLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    <FiX className="w-4 h-4" />
                    Reject
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && renderDetailsTab()}
        {activeTab === 'visits' && renderVisitsTab()}
        {activeTab === 'amounts' && renderAmountsTab()}
        {activeTab === 'images' && renderImagesTab()}
        {activeTab === 'logs' && <EntityLogs entity="cab_hiring" entityId={id as string} />
}

        {/* Visit Details Modal */}
        {showVisitModal && selectedVisit && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50"
              onClick={() => setShowVisitModal(false)}
            />
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl bg-white shadow-xl z-50 rounded max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Visit #{selectedVisit.visit_number} Details</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedVisit.project_site?.site_name || selectedVisit.visit_address || "No site linked"}
                  </p>
                </div>
                <button
                  onClick={() => setShowVisitModal(false)}
                  className="p-2 hover:bg-gray-200 rounded transition-colors"
                >
                  <FiX size={20} className="text-gray-600" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {/* Site Information */}
                <div className="bg-blue-50 border border-blue-200 rounded p-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-3">Site Information</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-blue-700 font-medium">Site Name:</span>
                      <p className="text-blue-900 mt-1">{selectedVisit.project_site?.site_name || "N/A"}</p>
                    </div>
                    <div>
                      <span className="text-blue-700 font-medium">Site ID:</span>
                      <p className="text-blue-900 mt-1">{selectedVisit.project_site?.site_id || "N/A"}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-blue-700 font-medium">Visit Time:</span>
                      <p className="text-blue-900 mt-1">{formatDate(selectedVisit.created_at)}</p>
                    </div>
                  </div>
                </div>

                {/* Location Information */}
                <div className="bg-white border border-gray-200 rounded p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FiMapPin className="w-4 h-4 text-gray-600" />
                    <h4 className="text-sm font-semibold text-gray-900">Location</h4>
                  </div>
                  {locationLoading ? (
                    <div className="text-center py-4">
                      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                      <p className="text-xs text-gray-500 mt-2">Fetching location...</p>
                    </div>
                  ) : visitLocation ? (
                    <div className="text-sm space-y-2">
                      <div>
                        <span className="text-gray-600 font-medium">Address:</span>
                        <p className="text-gray-900 mt-1">{visitLocation.formatted_address}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2 pt-2">
                        <div>
                          <span className="text-gray-600 text-xs">Latitude:</span>
                          <p className="text-gray-900 text-xs">{visitLocation.coordinates.lat}</p>
                        </div>
                        <div>
                          <span className="text-gray-600 text-xs">Longitude:</span>
                          <p className="text-gray-900 text-xs">{visitLocation.coordinates.lng}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Location information not available</p>
                  )}
                </div>

                {/* Visit Images */}
                <div className="bg-white border border-gray-200 rounded p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Visit Images</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedVisit.visit_selfie_with_cab && (
                      <div className="relative group">
                        <label className="text-xs font-medium text-gray-500 mb-2 block">Selfie with Cab</label>
                        <div className="relative overflow-hidden rounded border border-gray-200">
                          <img src={selectedVisit.visit_selfie_with_cab} alt="Selfie with Cab" className="w-full h-48 object-contain bg-gray-50" />
                          <button type="button" onClick={() => setLightboxSrc(selectedVisit.visit_selfie_with_cab)}
                            className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all">
                            <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-gray-800 text-xs font-medium px-3 py-1.5 rounded shadow-sm flex items-center gap-1">
                              <FiImage className="w-3 h-3" /> View Full Image
                            </span>
                          </button>
                        </div>
                      </div>
                    )}
                    {selectedVisit.visit_client_proof && (
                      <div className="relative group">
                        <label className="text-xs font-medium text-gray-500 mb-2 block">Client Proof</label>
                        <div className="relative overflow-hidden rounded border border-gray-200">
                          <img src={selectedVisit.visit_client_proof} alt="Client Proof" className="w-full h-48 object-contain bg-gray-50" />
                          <button type="button" onClick={() => setLightboxSrc(selectedVisit.visit_client_proof)}
                            className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all">
                            <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-gray-800 text-xs font-medium px-3 py-1.5 rounded shadow-sm flex items-center gap-1">
                              <FiImage className="w-3 h-3" /> View Full Image
                            </span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-t bg-gray-50 px-6 py-4">
                <button
                  onClick={() => setShowVisitModal(false)}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </>
        )}

        {/* Approval Drawer */}
        {showApprovalDrawer && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50"
              onClick={!actionLoading ? () => setShowApprovalDrawer(false) : undefined}
            />
            <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Approve Trip</h3>
                  <p className="text-sm text-gray-600 mt-1">Confirm approval of this cab hiring trip</p>
                </div>
                <button
                  onClick={() => setShowApprovalDrawer(false)}
                  className="p-2 hover:bg-gray-200 rounded transition-colors"
                  disabled={actionLoading}
                >
                  <FiX size={20} className="text-gray-600" />
                </button>
              </div>

              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded p-4">
                    <div className="flex items-start gap-3">
                      <FiCheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-green-900 mb-2">Trip Summary</p>
                        <div className="text-xs text-green-800 space-y-1">
                          <p>• Engineer: {trip?.engineer.first_name} {trip?.engineer.last_name}</p>
                          <p>• Distance: {trip.running_kms || 0} km</p>
                          <p>• Total Amount: {formatCurrency(getCabHiringTotalAmount(trip))}</p>
                          <p>• Visits: {trip.cab_visit?.length || 0}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payable Entity Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payable Entity *
                    </label>
                    <select
                      value={payableEntity}
                      onChange={(e) => {
                        setPayableEntity(e.target.value);
                        setPayableEntityName('');
                        setTouched({ ...touched, payableEntity: true });
                        if (errors.payableEntity) {
                          setErrors({ ...errors, payableEntity: '' });
                        }
                      }}
                      onBlur={() => {
                        setTouched({ ...touched, payableEntity: true });
                        const error = validatePayableEntity(payableEntity);
                        if (error) {
                          setErrors({ ...errors, payableEntity: error });
                        }
                      }}
                      disabled={actionLoading}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-green-500 ${
                        errors.payableEntity && touched.payableEntity
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300'
                      } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    >
                      <option value="">Select payable entity</option>
                      <option value="employee">Employee</option>
                      <option value="cab_vendor">Cab Vendor</option>
                    </select>
                    {errors.payableEntity && touched.payableEntity && (
                      <p className="mt-1 text-sm text-red-600">{errors.payableEntity}</p>
                    )}
                  </div>

                  {/* Cab Vendor Name (only if cab_vendor is selected) */}
                  {payableEntity === 'cab_vendor' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cab Vendor Name *
                      </label>
                      <input
                        type="text"
                        value={payableEntityName}
                        onChange={(e) => {
                          setPayableEntityName(e.target.value);
                          setTouched({ ...touched, payableEntityName: true });
                          if (errors.payableEntityName) {
                            setErrors({ ...errors, payableEntityName: '' });
                          }
                        }}
                        onBlur={() => {
                          setTouched({ ...touched, payableEntityName: true });
                          const error = validatePayableEntityName(payableEntityName, payableEntity);
                          if (error) {
                            setErrors({ ...errors, payableEntityName: error });
                          }
                        }}
                        disabled={actionLoading}
                        placeholder="Enter cab vendor name"
                        maxLength={100}
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-green-500 ${
                          errors.payableEntityName && touched.payableEntityName
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                      />
                      {errors.payableEntityName && touched.payableEntityName && (
                        <p className="mt-1 text-sm text-red-600">{errors.payableEntityName}</p>
                      )}
                    </div>
                  )}

                  {/* Approved Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Approved Amount (optional)
                    </label>
                    <input
                      type="number"
                      value={approvedAmount}
                      onChange={(e) => {
                        setApprovedAmount(e.target.value);
                        setTouched({ ...touched, approvedAmount: true });
                        if (errors.approvedAmount) {
                          setErrors({ ...errors, approvedAmount: '' });
                        }
                      }}
                      onBlur={() => {
                        setTouched({ ...touched, approvedAmount: true });
                        const totalAmount = getCabHiringTotalAmount(trip);
                        const error = validateApprovedAmount(approvedAmount, totalAmount);
                        if (error) {
                          setErrors({ ...errors, approvedAmount: error });
                        }
                      }}
                      disabled={actionLoading}
                      placeholder={`Max: ₹${getCabHiringTotalAmount(trip).toLocaleString('en-IN')}`}
                      min={0}
                      max={getCabHiringTotalAmount(trip)}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-green-500 ${
                        errors.approvedAmount && touched.approvedAmount
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300'
                      } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Leave blank to approve the full amount ({formatCurrency(getCabHiringTotalAmount(trip))})
                    </p>
                    {errors.approvedAmount && touched.approvedAmount && (
                      <p className="mt-1 text-sm text-red-600">{errors.approvedAmount}</p>
                    )}
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded p-4">
                    <div className="flex items-start gap-3">
                      <FiInfo className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Approval Guidelines</p>
                        <ul className="text-xs space-y-1 list-disc list-inside">
                          <li>Verify all trip details are accurate</li>
                          <li>Check that all visits have been documented</li>
                          <li>Ensure distance calculations are correct</li>
                          <li>Select appropriate payable entity</li>
                          <li>This action cannot be undone</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t bg-gray-50 px-6 py-4">
                  <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                    <button
                      onClick={() => setShowApprovalDrawer(false)}
                      disabled={actionLoading}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleApprove}
                      disabled={actionLoading || !payableEntity || (payableEntity === 'cab_vendor' && !payableEntityName.trim())}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading ? 'Approving...' : 'Approve Trip'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Reject Drawer */}
        {showRejectDrawer && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50"
              onClick={!actionLoading ? () => setShowRejectDrawer(false) : undefined}
            />
            <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Reject Trip</h3>
                  <p className="text-sm text-gray-600 mt-1">Provide a reason for rejection</p>
                </div>
                <button
                  onClick={() => setShowRejectDrawer(false)}
                  className="p-2 hover:bg-gray-200 rounded transition-colors"
                  disabled={actionLoading}
                >
                  <FiX size={20} className="text-gray-600" />
                </button>
              </div>

              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rejection Reason *
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => {
                        setRejectionReason(e.target.value);
                        setTouched({ ...touched, rejectionReason: true });
                        if (errors.rejectionReason) {
                          setErrors({ ...errors, rejectionReason: '' });
                        }
                      }}
                      onBlur={() => {
                        setTouched({ ...touched, rejectionReason: true });
                        const error = validateRejectionReason(rejectionReason);
                        if (error) {
                          setErrors({ ...errors, rejectionReason: error });
                        }
                      }}
                      rows={4}
                      disabled={actionLoading}
                      placeholder="Please provide a detailed reason for rejection (minimum 10 characters)..."
                      maxLength={500}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-red-500 ${errors.rejectionReason && touched.rejectionReason
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {rejectionReason.length}/500 characters (minimum 10)
                    </p>
                    {errors.rejectionReason && touched.rejectionReason && (
                      <p className="mt-1 text-sm text-red-600">{errors.rejectionReason}</p>
                    )}
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded p-4">
                    <div className="flex items-start gap-3">
                      <FiAlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-red-800">
                        <p className="font-medium mb-1">Rejection Guidelines</p>
                        <ul className="text-xs space-y-1 list-disc list-inside">
                          <li>Provide a clear and specific reason</li>
                          <li>Be professional and constructive</li>
                          <li>Use only alphanumeric characters and basic punctuation</li>
                          <li>This action cannot be undone</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t bg-gray-50 px-6 py-4">
                  <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                    <button
                      onClick={() => setShowRejectDrawer(false)}
                      disabled={actionLoading}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={actionLoading || !rejectionReason.trim()}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading ? 'Rejecting...' : 'Reject Trip'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Edit Submitted Cab Hiring Drawer ── */}
      {showEditDrawer && trip && trip.status === 'claim_submitted' && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowEditDrawer(false)} />
          <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div>
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <FiEdit className="text-primary-700" size={18} />
                  Edit Cab Hiring Claim
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">Only editable while status is "Submitted"</p>
              </div>
              <button onClick={() => setShowEditDrawer(false)} className="p-1.5 hover:bg-gray-200 rounded-lg">
                <FiX size={18} className="text-gray-500" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {/* Read-only summary */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-2 text-sm">
                <p><span className="text-gray-500 font-medium">Trip Purpose:</span> <span className="text-gray-900">{trip.trip_purpose}</span></p>
                <p><span className="text-gray-500 font-medium">Engineer:</span> <span className="text-gray-900">{trip.engineer.first_name} {trip.engineer.last_name}</span></p>
                <p><span className="text-gray-500 font-medium">Start Date:</span> <span className="text-gray-900">{formatDate(trip.trip_start_date)}</span></p>
                <p><span className="text-gray-500 font-medium">Running KMs:</span> <span className="text-gray-900">{trip.running_kms ?? 0} km</span></p>
                <p><span className="text-gray-500 font-medium">Per KM Rate:</span> <span className="text-gray-900">₹{trip.hiring_per_km_amount ?? 0}</span></p>
              </div>

              {/* Editable fields */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Toll Charges (₹)</label>
                <input
                  type="number"
                  min="0"
                  value={editForm.toll_charges}
                  onChange={e => setEditForm(f => ({ ...f, toll_charges: e.target.value }))}
                  placeholder="0"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2"
                />
                <p className="text-xs text-gray-400 mt-1">Current: ₹{trip.toll_charges ?? 0}</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Other Charges (₹)</label>
                <input
                  type="number"
                  min="0"
                  value={editForm.other_charges}
                  onChange={e => setEditForm(f => ({ ...f, other_charges: e.target.value }))}
                  placeholder="0"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2"
                />
                <p className="text-xs text-gray-400 mt-1">Current: ₹{trip.other_charges ?? 0}</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Remarks</label>
                <textarea
                  value={editForm.hiring_remarks}
                  onChange={e => setEditForm(f => ({ ...f, hiring_remarks: e.target.value }))}
                  placeholder="Add any remarks about the trip..."
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 resize-y"
                />
              </div>

              {/* Computed total preview */}
              <div className="bg-green-50 border border-green-100 rounded-lg p-4 text-sm">
                <p className="font-semibold text-gray-800 mb-1">Updated Total Preview</p>
                <div className="flex justify-between text-gray-700">
                  <span>Running ({trip.running_kms ?? 0} km × ₹{trip.hiring_per_km_amount ?? 0})</span>
                  <span>₹{Math.round(Number(trip.running_kms ?? 0) * Number(trip.hiring_per_km_amount ?? 0)).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Toll Charges</span>
                  <span>₹{(Number(editForm.toll_charges) || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Other Charges</span>
                  <span>₹{(Number(editForm.other_charges) || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between font-bold text-gray-900 border-t border-green-200 mt-2 pt-2">
                  <span>Total</span>
                  <span>₹{Math.round((Number(trip.running_kms ?? 0) * Number(trip.hiring_per_km_amount ?? 0)) + (Number(editForm.toll_charges) || 0) + (Number(editForm.other_charges) || 0)).toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowEditDrawer(false)}
                disabled={editLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={editLoading}
                className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
              >
                {editLoading ? (
                  <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> Saving...</>
                ) : (
                  <><FiSave size={14} /> Save Changes</>
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Lightbox modal */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[9999] bg-black bg-opacity-90 flex items-center justify-center p-4"
          onClick={() => setLightboxSrc(null)}
        >
          <button
            type="button"
            onClick={() => setLightboxSrc(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-2"
          >
            <FiX className="w-6 h-6" />
          </button>
          <img
            src={lightboxSrc}
            alt="Full size"
            className="max-w-full max-h-full object-contain rounded"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default CabHiringView;
